import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, List, ListItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSharedStyles } from '../style';
import { DiscussionItem } from '../types';
import { getForumDiscussions } from '../../../../../../../Utils/apiService';
import { getUserDetails } from '../../../../../../../storageService';
import dayjs from 'dayjs';

interface DiscussionLeftPanelProps {
    tabValue: number;
    onTabChange: (newValue: number) => void;
    selectedDiscussion: string | null;
    onDiscussionClick: (id: string, discussionId: string, discussionData?: any) => void;
    allCount: number;
    favoriteCount: number;
    forumId: number;
}

const useStyles = makeStyles(() => ({
    leftPanel: {
        width: '30%',
        overflow: 'auto',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #eaeaea',
        borderRadius: "12px !important"
    },
    tabsContainer: {
        backgroundColor: '#fff',
    },
    activeTab: {
        border: '1px solid #0174B7',
    },
    inactiveTab: {
        border: 'none',
    },
    discussionsList: {
        padding: '10px !important',
        overflowY: 'auto',
    },
    discussionItem: {
        padding: '15px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#f0f7ff',
        },
    },
    selectedDiscussionItem: {
        backgroundColor: '#e6f0ff',
        border: '1px solid #0174B7'
    },
    discussionItemDate: {
        textAlign: 'right',
        display: 'block',
        marginTop: '5px',
        fontSize: '12px',
        color: '#666',
    },
}));

const DiscussionLeftPanel: React.FC<DiscussionLeftPanelProps> = ({
    tabValue,
    onTabChange,
    selectedDiscussion,
    onDiscussionClick,
    allCount,
    favoriteCount,
    forumId,
}) => {
    const classes = useStyles();
    const sharedClasses = useSharedStyles();
    const userDetails = getUserDetails();

    const [discussions, setDiscussions] = useState<DiscussionItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedId, setSelectedId] = useState<string | null>(selectedDiscussion);

    const handleDiscussionClick = (id: string, discussionId: string) => {
        setSelectedId(id);
        const selectedDiscussion = discussions.find(discussion => discussion.id === id);
        onDiscussionClick(id, discussionId, selectedDiscussion);
    };

    const hasRun = React.useRef(false);

    const [allCountData, setAllCount] = useState<number>(0);

    useEffect(() => {
        if (!hasRun.current && forumId && userDetails?.ContactId) {
            hasRun.current = true;

            const fetchDiscussions = async () => {
                setLoading(true);
                try {
                    const response = await getForumDiscussions({
                        forumid: forumId,
                        contactid: userDetails.ContactId,
                    });

                    let discussionsData: DiscussionItem[] = [];

                    if (response?.discussions && Array.isArray(response.discussions)) {
                        discussionsData = response.discussions;
                    } else if (response?.success && response.data) {
                        discussionsData = response.data;
                    }

                    setDiscussions(discussionsData);
                    setAllCount(discussionsData.length);  // ✅ Update allCount dynamically

                    if (discussionsData.length > 0 && !selectedId) {
                        const firstItem = discussionsData[0];
                        setSelectedId(firstItem.id);
                        onDiscussionClick(firstItem.id, firstItem.discussionid, firstItem);
                    }
                } catch (error) {
                    console.error("Error fetching discussions:", error);
                    setDiscussions([]);
                    setAllCount(0);  // ✅ Set count to 0 if there's an error or no discussions
                } finally {
                    setLoading(false);
                }
            };

            fetchDiscussions();
        }
    }, [forumId, userDetails?.ContactId, onDiscussionClick, selectedId]);


    return (
        <Box className={classes.leftPanel}>
            <Box className={classes.tabsContainer} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    onClick={() => onTabChange(0)}
                    className={`${sharedClasses.font16500} ${tabValue === 0 ? classes.activeTab : classes.inactiveTab}`}
                    sx={{
                        padding: '10px 20px',
                        borderRadius: '0',
                        flex: '0 0 auto',
                        minWidth: 'auto',
                        textAlign: 'left',
                        backgroundColor: 'transparent',
                        color: "#373A45"
                    }}
                >
                    All ({allCountData})
                </Button>
                <Button
                    onClick={() => onTabChange(1)}
                    className={`${sharedClasses.font16500} ${tabValue === 1 ? classes.activeTab : classes.inactiveTab}`}
                    sx={{
                        padding: '10px 20px',
                        borderRadius: '0',
                        flex: '0 0 auto',
                        minWidth: 'auto',
                        textAlign: 'right',
                        backgroundColor: 'transparent',
                        color: "#373A45"
                    }}
                >
                    Favourite ({favoriteCount})
                </Button>
            </Box>

            {loading ? (
                <Box p={2}>
                    <Typography variant="body2" color="textSecondary" align="left">
                        Loading discussions...
                    </Typography>
                </Box>
            ) : (
                <List className={classes.discussionsList}>
                    {discussions.length > 0 ? (
                        discussions.map((discussion) => (
                            <ListItem
                                key={discussion.id}
                                className={`${classes.discussionItem} ${selectedId === discussion.id ? classes.selectedDiscussionItem : ''}`}
                                onClick={() => {
                                    console.log("Clicked discussion ID:", discussion.id);
                                    console.log("Clicked discussion Discussion ID:", discussion.discussionid);
                                    handleDiscussionClick(discussion.id, discussion.discussionid);
                                }}
                                sx={{
                                    padding: '15px',
                                    borderRadius: '12px',
                                }}
                            >
                                <Box width="100%" sx={{ px: 1 }}>
                                    <Typography variant="subtitle1" align="left">{discussion.name}</Typography>
                                    <Typography className={sharedClasses.font14400} align="left">
                                        {discussion.message}
                                    </Typography>
                                    <Typography className={classes.discussionItemDate}>
                                        {dayjs.unix(discussion.modified).format("DD MMMM YYYY")}
                                    </Typography>
                                </Box>
                            </ListItem>
                        ))
                    ) : (
                        <Box p={2}>
                            <Typography variant="body2" color="textSecondary" align="left">
                                No discussions available
                            </Typography>
                        </Box>
                    )}
                </List>
            )}
        </Box>
    );
};

export default DiscussionLeftPanel;